import React, { useRef, useEffect, useCallback } from 'react';

interface RoughProgressDotsProps {
  current: number;
  total: number;
  dotDiameter?: number;
  gap?: number;
  seed?: number;
}

const RoughProgressDots: React.FC<RoughProgressDotsProps> = ({
  current,
  total,
  dotDiameter = 16,
  gap = 12,
  seed = 50,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const totalWidth = total * dotDiameter + (total - 1) * gap;
  const canvasHeight = dotDiameter + 8; // extra padding for roughness

  const draw = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rough = (await import('roughjs')).default;

    const dpr = window.devicePixelRatio || 1;
    const w = totalWidth + 8; // extra padding
    const h = canvasHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);

    const rc = rough.canvas(canvas);
    const centerY = h / 2;

    for (let i = 0; i < total; i++) {
      const centerX = 4 + dotDiameter / 2 + i * (dotDiameter + gap);
      const isDone = i + 1 <= current;
      const isActive = i + 1 === current;

      // Active dot ring
      if (isActive) {
        rc.circle(centerX, centerY, dotDiameter + 8, {
          stroke: 'rgba(66,143,233,0.25)',
          strokeWidth: 1,
          roughness: 1.0,
          seed: seed + i * 10 + 100,
          fill: undefined,
        });
      }

      // Main dot
      rc.circle(centerX, centerY, dotDiameter, {
        stroke: isDone ? '#428fe9' : '#c4d1e3',
        strokeWidth: 1.8,
        roughness: 1.0,
        seed: seed + i * 10,
        fill: isActive
          ? 'rgba(66,143,233,0.15)'
          : isDone
            ? 'rgba(66,143,233,0.08)'
            : 'white',
        fillStyle: 'solid',
      });
    }
  }, [current, total, dotDiameter, gap, seed, totalWidth, canvasHeight]);

  useEffect(() => {
    draw();
  }, [draw]);

  // Redraw on resize
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ro = new ResizeObserver(() => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(draw, 300);
    });
    ro.observe(canvas.parentElement || canvas);

    return () => {
      ro.disconnect();
      clearTimeout(timerRef.current);
    };
  }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ display: 'block', margin: '0 auto' }}
    />
  );
};

export default RoughProgressDots;
